/**
 * DFSPH (Divergence-Free SPH) Compute Shaders
 *
 * DFSPH solver with 5 compute passes:
 * 1. computeDensityAlpha - Compute density and precompute alpha_i
 * 2. nonPressureForces - Gravity + viscosity + surface tension
 * 3. predictVelocity - v* = v + dt * (F/rho)
 * 4. divergenceSolveIteration - Divergence-free velocity correction (iterate 2x)
 * 5. densitySolveIteration - Constant density pressure correction (iterate 3x)
 *
 * Architecture:
 * - Uses spatial grid for neighbor search (27 cells)
 * - Alpha precomputation for pressure solve acceleration
 * - Two-stage pressure projection (divergence + density)
 * - WGSL compute shaders with @workgroup_size(256)
 */

import {
  SPH_STRUCT_TANIMLARI,
  SPH_KERNEL_FONKSIYONLARI,
  SPH_GRID_YARDIMCI,
  SPH_ORTAK_BINDING_TANIMLARI,
  buildShader
} from './sph-ortak-shaders.wgsl';

/**
 * DFSPH Solver-Specific Bindings (@group(3))
 */
export const DFSPH_SOLVER_BINDINGS = `
// DFSPH solver-specific buffers
@group(3) @binding(0) var<storage, read_write> alpha: array<f32>;          // precomputed alpha per particle
@group(3) @binding(1) var<storage, read_write> densityAdv: array<f32>;     // predicted/advanced density
`;

/**
 * Pass 1: Compute Density and Alpha
 *
 * Computes density rho_i and precomputes alpha_i for pressure solve.
 * Alpha is the denominator term used in DFSPH pressure correction.
 */
export const DFSPH_COMPUTE_DENSITY_ALPHA = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${DFSPH_SOLVER_BINDINGS}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
  let i = global_id.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let h = params.smoothingRadius;
  let h2 = h * h;
  let useWendland = params.kernelTipi == 1u;

  // Wendland C2 support radius = 2h (vs Poly6/Spiky = h)
  let searchR2 = select(h2, 4.0 * h2, useWendland);

  // Precompute kernel constants (classic)
  let poly6C = 315.0 / (64.0 * 3.14159265359 * pow(h, 9.0));
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));

  // Particle mass (uniform)
  let particleMass = params.restDensity * pow(h, 3.0);

  // Compute density
  var rho_i = 0.0;

  // Alpha computation
  var sum1 = vec3<f32>(0.0);
  var sum2 = 0.0;

  // Grid-based neighbor search (27 cells)
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);

            if (useWendland) {
              rho_i += particleMass * kernelWendlandC2(r, h);
              let grad = particleMass * kernelWendlandC2Grad(r_ij, r, h);
              sum1 += grad;
              sum2 += dot(grad, grad);
            } else {
              rho_i += particleMass * kernelPoly6(r2, h, poly6C);
              let grad = particleMass * kernelSpikyGrad(r_ij, r, h, spikyC);
              sum1 += grad;
              sum2 += dot(grad, grad);
            }
          }
        }
      }
    }
  }

  // Self-contribution to density
  if (useWendland) {
    rho_i += particleMass * kernelWendlandC2(0.0, h);
  } else {
    rho_i += particleMass * kernelPoly6(0.0, h, poly6C);
  }

  // Compute alpha_i
  let denom = dot(sum1, sum1) + sum2;
  let alpha_val = select(-rho_i * rho_i / denom, 0.0, denom < 1e-6);

  // Store results
  alpha[i] = alpha_val;
  densityMisc[i].x = rho_i;
}
`);

/**
 * Pass 2: Non-Pressure Forces
 *
 * Computes gravity, viscosity, and surface tension forces.
 * Stores result in forceVmax buffer.
 */
export const DFSPH_NON_PRESSURE_FORCES = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${DFSPH_SOLVER_BINDINGS}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
  let i = global_id.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let vel_i = velocityAge[i].xyz;
  let rho_i = densityMisc[i].x;
  let p_i = densityMisc[i].y;
  let h = params.smoothingRadius;
  let h2 = h * h;
  let useWendland = params.kernelTipi == 1u;

  let searchR2 = select(h2, 4.0 * h2, useWendland);

  // Precompute kernel constants
  let viscC = 45.0 / (3.14159265359 * pow(h, 6.0));
  let cohC = 32.0 / (3.14159265359 * pow(h, 9.0));
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));

  // Tensile correction reference value (Wendland at particle spacing)
  let W_ref = wendlandRefValue(h);

  // Particle mass
  let particleMass = params.restDensity * pow(h, 3.0);

  // Force accumulator
  var F = vec3<f32>(0.0);

  // Grid-based neighbor search
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let vel_j = velocityAge[j].xyz;
          let rho_j = densityMisc[j].x;
          let p_j = densityMisc[j].y;
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);

            // Viscosity force: lap_W * (v_j - v_i) * mu * m_j / rho_j
            let viscLap = kernelViscLap(r, h, viscC);
            F += particleMass / rho_j * params.viscosity * (vel_j - vel_i) * viscLap;

            // Surface tension (Akinci cohesion)
            if (r > 1e-6) {
              let cohesion = kernelCohesion(r, h, cohC);
              F -= params.surfaceTension * particleMass * particleMass * cohesion * normalize(r_ij);
            }

            // Tensile instability correction (Wendland C2 mode)
            if (useWendland) {
              let W_ij = kernelWendlandC2(r, h);
              let s_corr = tensileCorrection(W_ij, W_ref);
              let grad_w = kernelWendlandC2Grad(r_ij, r, h);

              // Apply tensile correction when either particle has negative pressure
              let pi_neg = select(0.0, p_i / (rho_i * rho_i), p_i < 0.0);
              let pj_neg = select(0.0, p_j / (rho_j * rho_j), p_j < 0.0);
              F += particleMass * (pi_neg + pj_neg + s_corr) * grad_w;
            }
          }
        }
      }
    }
  }

  // Store force (gravity will be added in predict step)
  forceVmax[i] = vec4<f32>(F, 0.0);
}
`);

/**
 * Pass 3: Predict Velocity
 *
 * Advects velocity by non-pressure forces and gravity.
 * v* = v + dt * (F/rho + g)
 */
export const DFSPH_PREDICT_VELOCITY = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${DFSPH_SOLVER_BINDINGS}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
  let i = global_id.x;
  if (i >= params.activeCount) { return; }

  let vel_i = velocityAge[i].xyz;
  let F = forceVmax[i].xyz;
  let rho_i = densityMisc[i].x;

  // Gravity
  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);

  // Predict velocity
  let v_star = vel_i + params.dt * (F / rho_i + gravity);

  // Store predicted velocity
  velocityAge[i] = vec4<f32>(v_star, velocityAge[i].w);
}
`);

/**
 * Pass 4: Divergence Solve Iteration
 *
 * Corrects velocity to be divergence-free.
 * Iteratively enforces: div(v) = 0
 * Typically run 2 iterations per timestep.
 */
export const DFSPH_DIVERGENCE_SOLVE = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${DFSPH_SOLVER_BINDINGS}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
  let i = global_id.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let vel_i = velocityAge[i].xyz;
  let rho_i = densityMisc[i].x;
  let alpha_i = alpha[i];
  let h = params.smoothingRadius;
  let h2 = h * h;
  let useWendland = params.kernelTipi == 1u;
  let searchR2 = select(h2, 4.0 * h2, useWendland);

  // Precompute kernel constants
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));
  let particleMass = params.restDensity * pow(h, 3.0);

  // Compute divergence (density change rate)
  var drho_dt = 0.0;

  // Grid-based neighbor search
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let vel_j = velocityAge[j].xyz;
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);
            let v_ij = vel_i - vel_j;
            var grad: vec3<f32>;
            if (useWendland) {
              grad = particleMass * kernelWendlandC2Grad(r_ij, r, h);
            } else {
              grad = particleMass * kernelSpikyGrad(r_ij, r, h, spikyC);
            }
            drho_dt += dot(v_ij, grad);
          }
        }
      }
    }
  }

  // Compute velocity correction coefficient
  let kv = drho_dt * alpha_i / params.dt;

  // Compute velocity correction
  var v_correction = vec3<f32>(0.0);

  // Second neighbor loop for correction
  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let rho_j = densityMisc[j].x;
          let alpha_j = alpha[j];
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);
            var grad: vec3<f32>;
            if (useWendland) {
              grad = particleMass * kernelWendlandC2Grad(r_ij, r, h);
            } else {
              grad = particleMass * kernelSpikyGrad(r_ij, r, h, spikyC);
            }

            // Symmetric pressure term
            let term = particleMass * (kv / (rho_i * rho_i) + alpha_j * drho_dt / (params.dt * rho_j * rho_j));
            v_correction += term * grad;
          }
        }
      }
    }
  }

  // Apply velocity correction
  let vel_new = vel_i - params.dt * v_correction;
  velocityAge[i] = vec4<f32>(vel_new, velocityAge[i].w);
}
`);

/**
 * Pass 5: Density Solve Iteration
 *
 * Corrects velocity for constant density constraint.
 * Iteratively enforces: rho = rho_0 (rest density)
 * Typically run 3-5 iterations per timestep.
 */
export const DFSPH_DENSITY_SOLVE = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${DFSPH_SOLVER_BINDINGS}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
  let i = global_id.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let vel_i = velocityAge[i].xyz;
  let rho_i = densityMisc[i].x;
  let alpha_i = alpha[i];
  let h = params.smoothingRadius;
  let h2 = h * h;
  let useWendland = params.kernelTipi == 1u;
  let searchR2 = select(h2, 4.0 * h2, useWendland);

  // Precompute kernel constants
  let spikyC = -45.0 / (3.14159265359 * pow(h, 6.0));
  let particleMass = params.restDensity * pow(h, 3.0);

  // Predict density after advection
  var drho_dt = 0.0;

  // Grid-based neighbor search
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let vel_j = velocityAge[j].xyz;
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);
            let v_ij = vel_i - vel_j;
            var grad: vec3<f32>;
            if (useWendland) {
              grad = particleMass * kernelWendlandC2Grad(r_ij, r, h);
            } else {
              grad = particleMass * kernelSpikyGrad(r_ij, r, h, spikyC);
            }
            drho_dt += dot(v_ij, grad);
          }
        }
      }
    }
  }

  // Advanced density
  let rho_adv = rho_i + params.dt * drho_dt;
  densityAdv[i] = rho_adv;

  // Pressure coefficient
  let kp = (rho_adv - params.restDensity) * alpha_i;

  // Compute velocity correction
  var v_correction = vec3<f32>(0.0);

  // Second neighbor loop for correction
  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let rho_j = densityMisc[j].x;
          let rho_adv_j = densityAdv[j];
          let alpha_j = alpha[j];
          let r_ij = pos_i - pos_j;
          let r2 = dot(r_ij, r_ij);

          if (r2 < searchR2 && r2 > 1e-9) {
            let r = sqrt(r2);
            var grad: vec3<f32>;
            if (useWendland) {
              grad = particleMass * kernelWendlandC2Grad(r_ij, r, h);
            } else {
              grad = particleMass * kernelSpikyGrad(r_ij, r, h, spikyC);
            }

            // Symmetric pressure term
            let kp_j = (rho_adv_j - params.restDensity) * alpha_j;
            let term = particleMass * (kp / (rho_i * rho_i) + kp_j / (rho_j * rho_j));
            v_correction += term * grad;
          }
        }
      }
    }
  }

  // Apply velocity correction
  let vel_new = vel_i - params.dt * v_correction;
  velocityAge[i] = vec4<f32>(vel_new, velocityAge[i].w);
}
`);

/**
 * Shader Metadata
 *
 * Describes the DFSPH solver passes and their buffer requirements.
 */
export const DFSPH_SHADER_METADATA = {
  passes: [
    {
      name: 'computeDensityAlpha',
      shader: DFSPH_COMPUTE_DENSITY_ALPHA,
      workgroupSize: 256,
      description: 'Compute density and precompute alpha for pressure solve'
    },
    {
      name: 'nonPressureForces',
      shader: DFSPH_NON_PRESSURE_FORCES,
      workgroupSize: 256,
      description: 'Compute gravity, viscosity, and surface tension forces'
    },
    {
      name: 'predictVelocity',
      shader: DFSPH_PREDICT_VELOCITY,
      workgroupSize: 256,
      description: 'Advect velocity by non-pressure forces'
    },
    {
      name: 'divergenceSolve',
      shader: DFSPH_DIVERGENCE_SOLVE,
      workgroupSize: 256,
      iterations: 2,
      description: 'Divergence-free velocity correction (iterate 2x)'
    },
    {
      name: 'densitySolve',
      shader: DFSPH_DENSITY_SOLVE,
      workgroupSize: 256,
      iterations: 3,
      description: 'Constant density pressure correction (iterate 3-5x)'
    }
  ],
  bufferBindings: {
    group0: ['params'],
    group1: ['positionMass', 'velocityAge', 'forceVmax', 'densityMisc'],
    group2: ['prefixSum', 'sortedIndex'],
    group3: ['alpha', 'densityAdv']
  },
  description: 'DFSPH (Divergence-Free SPH) solver with two-stage pressure projection'
};
